
import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { RequestImageUpload } from '@/components/services/RequestImageUpload';

const requestFormSchema = z.object({
  title: z.string().min(1, 'Title is required'),
  description: z.string().min(1, 'Description is required'),
  service_category_id: z.string().min(1, 'Category is required'),
  budget: z.string().optional().refine((val) => !val || parseFloat(val) >= 5, {
    message: "Budget must be at least $5",
  }),
});

type RequestFormData = z.infer<typeof requestFormSchema>;

interface ServiceCategory {
  id: string;
  name: string;
}

interface ServiceRequest {
  id: string;
  title: string;
  description: string;
  service_category_id: string | null;
  pricing_type: string;
  budget: number | null;
  image_url: string | null;
}

export const EditRequestForm = () => {
  const { id } = useParams<{ id: string }>();
  const [categories, setCategories] = useState<ServiceCategory[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [request, setRequest] = useState<ServiceRequest | null>(null);
  const { toast } = useToast();
  const navigate = useNavigate();

  const form = useForm<RequestFormData>({
    resolver: zodResolver(requestFormSchema),
    defaultValues: {
      title: '',
      description: '',
      service_category_id: '',
      budget: '',
    },
  });

  useEffect(() => {
    fetchCategories();
    if (id) {
      fetchRequest();
    }
  }, [id]);

  const fetchCategories = async () => {
    try {
      const { data, error } = await supabase
        .from('service_categories')
        .select('id, name')
        .order('name');

      if (error) {
        console.error('Error fetching categories:', error);
        toast({
          title: 'Error',
          description: 'Failed to load service categories',
          variant: 'destructive',
        });
        return;
      }

      setCategories(data || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
      toast({
        title: 'Error',
        description: 'Failed to load service categories',
        variant: 'destructive',
      });
    }
  };

  const fetchRequest = async () => {
    if (!id) return;
    
    try {
      const { data, error } = await supabase
        .from('service_requests')
        .select('*')
        .eq('id', id)
        .single();

      if (error) {
        console.error('Error fetching request:', error);
        toast({
          title: 'Error',
          description: 'Failed to load request',
          variant: 'destructive',
        });
        navigate('/my-services');
        return;
      }

      setRequest(data);
      setImageUrl(data.image_url);
      
      // Update form with existing data
      form.reset({
        title: data.title,
        description: data.description,
        service_category_id: data.service_category_id || '',
        budget: data.budget ? data.budget.toString() : '',
      });
    } catch (error) {
      console.error('Error fetching request:', error);
      toast({
        title: 'Error',
        description: 'Failed to load request',
        variant: 'destructive',
      });
      navigate('/my-services');
    }
  };

  const onSubmit = async (data: RequestFormData) => {
    if (!id) return;
    
    setIsLoading(true);
    
    try {
      const requestData = {
        title: data.title,
        description: data.description,
        service_category_id: data.service_category_id,
        pricing_type: 'fixed',
        budget: data.budget ? parseFloat(data.budget) : null,
        image_url: imageUrl,
        updated_at: new Date().toISOString(),
      };

      const { error } = await supabase
        .from('service_requests')
        .update(requestData)
        .eq('id', id);

      if (error) {
        console.error('Error updating request:', error);
        toast({
          title: 'Error',
          description: 'Failed to update request. Please try again.',
          variant: 'destructive',
        });
        return;
      }

      toast({
        title: 'Success',
        description: 'Request updated successfully!',
      });

      navigate('/my-services?tab=requests');
    } catch (error) {
      console.error('Error updating request:', error);
      toast({
        title: 'Error',
        description: 'An unexpected error occurred. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (!request) {
    return (
      <div className="container mx-auto p-6 flex justify-center items-center min-h-[50vh]">
        <div className="text-center">
          <div className="animate-spin w-12 h-12 border-4 border-anyskill-purple border-t-transparent rounded-full mx-auto"></div>
          <p className="mt-4 text-lg text-gray-600">Loading request...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-2xl">
      <div className="mb-8">
        <h1 className="text-3xl font-bold">Edit Service Request</h1>
        <p className="text-gray-600 mt-2">Update your service request details</p>
      </div>
      
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <FormField
            control={form.control}
            name="service_category_id"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Service Category</FormLabel>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a category" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {categories.map((category) => (
                      <SelectItem key={category.id} value={category.id}>
                        {category.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="title"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Title</FormLabel>
                <FormControl>
                  <Input placeholder="Enter request title" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="description"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Description</FormLabel>
                <FormControl>
                  <Textarea 
                    placeholder="Describe your service request..."
                    className="min-h-[100px]"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <RequestImageUpload
            currentImageUrl={imageUrl}
            onImageChange={setImageUrl}
          />

          <FormField
            control={form.control}
            name="budget"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Budget (Optional)</FormLabel>
                <FormControl>
                  <Input 
                    type="number" 
                    step="0.01"
                    placeholder="Enter your budget"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="flex gap-4">
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => navigate('/my-services?tab=requests')}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading} className="flex-1">
              {isLoading ? 'Updating...' : 'Update Request'}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
};

export default EditRequestForm;
